/**
 * Live Location Map Component
 * Displays agent locations on an interactive map using Leaflet + OpenStreetMap.
 * Features:
 * - Real-time agent location markers with status colors
 * - Geofence visualization
 * - Agent info popup on marker click
 * - Auto-refresh functionality
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import HelpInfo from './HelpInfo';
import { 
  RefreshCw, 
  MapPin, 
  Users,
  Maximize2,
  Minimize2
} from 'lucide-react';

// Fix Leaflet default marker icon issue
import 'leaflet/dist/leaflet.css';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Custom marker icons for different statuses
const createStatusIcon = (status) => {
  const colors = {
    inside: '#22c55e',   // Green
    outside: '#ef4444',  // Red
    on_leave: '#3b82f6'  // Blue
  };
  
  const color = colors[status] || '#6b7280';
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 30px;
        height: 30px;
        background-color: ${color};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 5px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30]
  });
};

// Map bounds fitter component
const MapBoundsFitter = ({ agents, geofences }) => {
  const map = useMap();
  
  useEffect(() => {
    const points = [];
    
    // Add agent positions
    agents.forEach(agent => {
      if (agent.last_lat && agent.last_lng) {
        points.push([agent.last_lat, agent.last_lng]);
      }
    });
    
    // Add geofence centers
    geofences.forEach(geo => {
      if (geo.center_lat && geo.center_lng) {
        points.push([geo.center_lat, geo.center_lng]);
      }
    });
    
    if (points.length > 0) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [agents, geofences, map]);
  
  return null;
};

// roleScope controls who appears on this map:
//   'all'             — everyone (agents + HODs + CEO)
//   'agents_and_hods' — agents + HOD bench, no CEO (CEO dashboard default)
//   'agents_only'     — agents only, no HODs or CEO (HOD dashboard default)
const HOD_ROLES = new Set(['hod', 'hod_sales', 'hod_marketing']);
const MGMT_ROLES = new Set([...HOD_ROLES, 'ceo']);

const LiveLocationMap = ({
  compact = false,
  agentTypeFilter = 'all',
  showControls = true,
  roleScope = 'all',
}) => {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  const [agents, setAgents] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [office, setOffice] = useState({ is_open: true });

  // Default center (Dubai)
  const defaultCenter = [25.2048, 55.2708];

  const fetchData = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      // Fetch agents
      const agentParams = new URLSearchParams({ token });
      if (agentTypeFilter && agentTypeFilter !== 'all') {
        agentParams.append('agent_type', agentTypeFilter);
      }
      
      const [agentsRes, geofencesRes] = await Promise.all([
        fetch(`${API_BASE}/api/agent-status/all?${agentParams}`),
        fetch(`${API_BASE}/api/agent-status/geofences?token=${token}`)
      ]);
      
      const agentsData = await agentsRes.json();
      const geofencesData = await geofencesRes.json();
      
      if (agentsData.success) {
        // Office-hours metadata controls whether the map shows GPS at all.
        if (agentsData.office) setOffice(agentsData.office);
        // Apply the dashboard-specific role scope before anything else: HODs
        // see only agents (so they aren't tracking each other or the CEO);
        // the CEO sees agents + HODs but not themselves.
        const scoped = (agentsData.agents || []).filter((a) => {
          const role = (a.role || '').toLowerCase();
          if (roleScope === 'agents_only') {
            return !MGMT_ROLES.has(role);
          }
          if (roleScope === 'agents_and_hods') {
            return role !== 'ceo';
          }
          return true; // 'all'
        });
        const allAgents = scoped.map((a) => ({
          ...a,
          last_lat: a.lat,
          last_lng: a.lng,
        }));
        setAgents(allAgents);
      }
      
      if (geofencesData.success) {
        setGeofences(geofencesData.geofences || []);
      }
      
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching location data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, agentTypeFilter]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Calculate map center from agents or use default
  const mapCenter = useMemo(() => {
    const validAgents = agents.filter(a => a.last_lat && a.last_lng);
    if (validAgents.length > 0) {
      const avgLat = validAgents.reduce((sum, a) => sum + a.last_lat, 0) / validAgents.length;
      const avgLng = validAgents.reduce((sum, a) => sum + a.last_lng, 0) / validAgents.length;
      return [avgLat, avgLng];
    }
    if (geofences.length > 0 && geofences[0].center_lat) {
      return [geofences[0].center_lat, geofences[0].center_lng];
    }
    return defaultCenter;
  }, [agents, geofences]);

  const getStatusLabel = (status) => {
    const labels = {
      inside: 'In Office',
      outside: 'Outside',
      on_leave: 'On Leave'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      inside: 'bg-green-500',
      outside: 'bg-red-500',
      on_leave: 'bg-blue-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const mapHeight = compact ? 'h-64' : isExpanded ? 'h-[600px]' : 'h-96';

  // Live markers only when both: office is open AND agent is not on leave.
  const visibleAgents = agents.filter(
    (a) => office.is_open && !a.on_leave && a.last_lat && a.last_lng
  );
  const onLeaveCount = agents.filter((a) => a.on_leave).length;

  return (
    <Card className={isDarkMode ? 'bg-card border-border' : ''} data-testid="live-location-map">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className={`h-5 w-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
            <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
              {roleScope === 'agents_only'
                ? 'Live Agent Tracker'
                : roleScope === 'agents_and_hods'
                  ? 'Live Tracker · Agents & HODs'
                  : 'Live Location Tracker'}
              <HelpInfo
                align="inline"
                title="How to read the Live Tracker"
                steps={[
                  'Each pin is a team member with a recent GPS signal. Click a pin to see their name, last-known location time, and any active site visit.',
                  <>The numbers next to the title show how many people are <em>live</em> (broadcasting GPS within the last few minutes) versus <em>on leave</em> (approved leave today — no pin).</>,
                  'Use the controls in the corner to zoom, refresh, or expand to full screen. The map auto-refreshes every minute.',
                  <>Pins fade as the GPS signal goes stale — a faded pin usually means the agent\'s phone is offline or the location permission was revoked. A solid pin is live data.</>,
                ]}
                tip="If you see clusters of pins all at the office, the team's in. If pins spread across the map by mid-morning, that's a good day in the field."
              />
            </CardTitle>
            <Badge variant="outline" className="ml-2">
              {visibleAgents.length} live
            </Badge>
            {onLeaveCount > 0 && (
              <Badge variant="outline" className="ml-1 text-blue-600 border-blue-300">
                {onLeaveCount} on leave
              </Badge>
            )}
            {!office.is_open && (
              <Badge variant="outline" className="ml-1 text-amber-700 border-amber-300 bg-amber-50">
                Office closed · tracking paused
              </Badge>
            )}
          </div>
          {showControls && (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={fetchData}
                disabled={loading}
                title="Refresh"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              {!compact && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsExpanded(!isExpanded)}
                  title={isExpanded ? 'Collapse' : 'Expand'}
                >
                  {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
              )}
            </div>
          )}
        </div>
        {lastRefresh && (
          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Last updated: {lastRefresh.toLocaleTimeString()}
            {office?.office_hours_start && office?.office_hours_end && (
              <> · Office hours {office.office_hours_start}–{office.office_hours_end} ({office.timezone || 'local'})</>
            )}
          </p>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <div className={`${mapHeight} rounded-b-lg overflow-hidden`}>
          <MapContainer
            center={mapCenter}
            zoom={12}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={!compact}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url={isDarkMode 
                ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              }
            />
            
            {/* Geofence circles */}
            {geofences.map((geo, idx) => (
              <Circle
                key={`geo-${idx}`}
                center={[geo.center_lat, geo.center_lng]}
                radius={geo.radius_meters || 200}
                pathOptions={{
                  color: '#8b5cf6',
                  fillColor: '#8b5cf6',
                  fillOpacity: 0.1,
                  weight: 2,
                  dashArray: '5, 5'
                }}
              >
                <Popup>
                  <div className="text-center">
                    <strong>{geo.name || 'Office'}</strong>
                    <br />
                    <span className="text-sm text-gray-500">
                      Radius: {geo.radius_meters || 200}m
                    </span>
                  </div>
                </Popup>
              </Circle>
            ))}
            
            {/* Agent markers — only when office is open and agent is not on leave */}
            {visibleAgents.map((agent) => (
              <Marker
                key={agent.agent_id}
                position={[agent.last_lat, agent.last_lng]}
                icon={createStatusIcon(agent.status)}
              >
                <Popup>
                  <div className="min-w-[150px]">
                    <div className="flex items-center gap-2 mb-2">
                      {agent.picture ? (
                        <img 
                          src={agent.picture} 
                          alt={agent.full_name}
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <Users className="w-4 h-4 text-gray-500" />
                        </div>
                      )}
                      <div>
                        <strong className="block text-sm">{agent.full_name}</strong>
                        <span className="text-xs text-gray-500">{agent.email}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-block w-2 h-2 rounded-full ${getStatusColor(agent.status)}`}></span>
                      <span className="text-sm">{getStatusLabel(agent.status)}</span>
                    </div>
                    {agent.agent_type && (
                      <Badge variant="outline" className="mt-2 text-xs">
                        {agent.agent_type === 'direct' ? 'Direct' : 'Indirect'} Agent
                      </Badge>
                    )}
                    {agent.last_updated && (
                      <p className="text-xs text-gray-400 mt-2">
                        Updated: {new Date(agent.last_updated).toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
            
            <MapBoundsFitter agents={agents} geofences={geofences} />
          </MapContainer>
        </div>

        {/* Legend */}
        {!compact && (
          <div className={`px-4 py-3 border-t flex items-center gap-4 ${
            isDarkMode ? 'border-gray-700' : 'border-gray-100'
          }`}>
            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Status:</span>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-green-500"></span>
              <span className="text-xs">In Office</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              <span className="text-xs">Outside</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-blue-500"></span>
              <span className="text-xs">On Leave</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LiveLocationMap;
